import { useCallback, useState, useEffect } from 'react';
import throttle from 'lodash.throttle';

export type ScrollPosition = {
  x: number;
  y: number;
};

export const DEFAULT_WAIT_TIME = 100;

const getScrollPosition = (
  element: HTMLElement | Window | null,
): ScrollPosition => {
  if (!element) {
    return { x: 0, y: 0 };
  }
  if (element instanceof Window) {
    return { x: window.scrollX, y: window.scrollY };
  }
  return { x: element.scrollLeft, y: element.scrollTop };
};

/**
 * A custom hook to track the scroll position of an element or the window.
 *
 * @param {HTMLElement | Window | null} [element=null] - The target element to track its scroll position. If not provided, the window scroll position will be tracked.
 * @param {number} [waitTime=DEFAULT_WAIT_TIME] - The wait time (in milliseconds) for throttling the scroll event. Default value is set by the DEFAULT_WAIT_TIME constant.
 * @returns {ScrollPosition} - The current scroll position of the target element or the window.
 *
 * @example
 * const scrollPosition = useScrolling(elementRef.current, 100);
 */
export const useScrolling = (
  element: HTMLElement | Window | null = null,
  waitTime = DEFAULT_WAIT_TIME,
): ScrollPosition => {
  const [scrollPosition, setScrollPosition] = useState<ScrollPosition>(() =>
    getScrollPosition(element),
  );

  const onScroll = (): void => {
    const position = getScrollPosition(element);
    setScrollPosition(position);
  };

  const throttledOnScroll = useCallback(
    throttle(onScroll, waitTime, {
      leading: true,
      trailing: true,
    }),
    [waitTime, element],
  );

  useEffect(() => {
    element?.addEventListener('scroll', throttledOnScroll);

    return (): void => {
      element?.removeEventListener('scroll', throttledOnScroll);
      throttledOnScroll.cancel();
    };
  }, [element, throttledOnScroll]);

  return scrollPosition;
};
